// 版面幾何矩陣：`phase × inline/popup × 有無 hero × 斷點` 對應的面板目標高、header 目標高，
// 以及每個 phase 由誰擁有垂直捲動。以前這些只以 CSS 規則的形式存在，沒有可呼叫的 interface ——
// 測試只能對 cssText 做 regex，於是把 class 改個名會紅、把 692 改成 629 反而綠。
//
// 這裡是資料，CSS 由 geometryCss() 產生。所有面板高都是 border-box 的 Figma 目標值，且一律
// 夾在同一個 viewport 上限內。
//
// 生成出來的字串裡一個 CSS 註解都不放 —— build/strip-css-literal-comments 只處理 css`` 模板，
// 放進生成字串的註解會原樣進 bundle。（原本那些註解在 css`` 裡本就被剝掉，所以這不是省，是不
// 要製造新成本；這一刀整體讓 bundle 略為變大，數字在 PR #148。）

// 面板 phase。frame 的內部渲染狀態，與 state.ts 的 Phase 是兩回事（那個是元件的載入狀態機）。
export type FrameView = 'answering' | 'results' | 'thank-you'
// inline 內文嵌入 vs popup（sidebar / modal / manual 共用的彈出殼）。
export type FrameVariant = 'inline' | 'popup'
// 誰擁有垂直捲動。一個 phase 恰好一個擁有者 —— 這是型別本身保證的，不是四條各自獨立的 CSS。
// （收合卡是「誰都不捲」的第三種，住在 sidebar-shell.ts，尚未納入本表。）
export type ScrollOwner = 'body' | 'frame'

// 斷點：viewport ≥640px。注意 frame.ts 另有 `@container frame (min-width: 640px)`，那是容器寬
// 的軸、管的是內距與字級，跟這裡不是同一件事（同一個數字，不同觸發條件）。
const WIDE_PX = 640
// 面板高一律夾在這個 viewport 比例內。vh 先於 dvh 是刻意的：舊 WebView 不認 dvh，先寫 vh 才有
// 東西可退。
const VIEWPORT_CAP_PCT = 90
// answering 的 header 最多只能吃到「面板高 - 這個值」，剩下的保留給題目本身。
//
// 這是題目區的絕對下限，不是從哪個版型的面板高推導出來的，所以 inline 與 popup 共用同一個數字、
// 不加版型排除。popup 的面板是固定高，百分比算得出來（popup 638/756 減 320 後 header 還有
// 318/436）；inline 的面板跟內容長（capped），高度是 auto、百分比不成立，所以改以上限為基準
// 算同一個數字（上限 530/528 加 logo 列，減 320 後 header 剩 210/208 加 logo 列）。這條保護不能
// 因為 inline 看起來數字小就拿掉：.frame 是 overflow:hidden、header 是 flex:none，body 的
// flex:1 1 auto + overflow-y:auto 接不住高度被壓到 0 的框，長標題不再只是被截字，而是整份問卷消失。
const BODY_RESERVE_PX = 320

interface ByBreakpoint<T = number> { narrow: T, wide: T }

// IP logo 在版面上的尺寸。高度一律＝寬 × --mp-logo-aspect（每個 brand 的值由 design-system/logos.ts
// 依 .frame[data-brand] 產生）。frame.ts 的 .frame-logo 寬與 margin 由 frame.test.ts 釘到這裡。
const LOGO = {
  // in-flow 寬：popup 窄版、收合卡、inline（Figma 314:4171 / 314:3461 / 298:2557）。
  inFlowWidth: 105,
  // logo 與下一列的距離：popup／收合卡 12（Figma 314:4171 logo＋badge 容器 gap 12）；
  // inline 16（Figma 298:2557：Logo y20 高 19.09，Questionnaire Container y55.09）。
  rowGap: { popup: 12, inline: 16 },
}

// 一個 phase 的面板高怎麼決定。四種各自代表不同的事，不共用一個 null：
//   fixed   —— 一組固定值。
//   by-hero —— 再依有無 hero 分（hero 圖本身要佔掉高度）。
//   capped  —— 跟內容長、有上限：不輸出 --target-panel-height（height 那條因變數未定義而整條失效，
//              與 auto 同一個機制），改輸出 --target-panel-cap，再由 max-height 夾住。今天只有
//              inline 的作答頁是這種：面板高 = 整份問卷最高那題的自然高，上限 530/528 加 logo 列。
//   auto    —— 刻意不設固定高也不設上限：面板跟著內文長。今天是 inline 的結果頁與致謝頁。
type PanelSpec
  = | { kind: 'fixed', height: ByBreakpoint }
    | { kind: 'by-hero', hero: ByBreakpoint, plain: ByBreakpoint }
    | { kind: 'capped', cap: ByBreakpoint }
    | { kind: 'auto' }
// popup 沒有「跟內容長、有上限」這一格：popupRules 的窮舉由型別保證，不會有 popup 的 phase 悄悄落到
// 沒有任何規則的分支。
type PopupPanelSpec = Exclude<PanelSpec, { kind: 'capped' }>

// 兩層 Record 都是窮盡的：多一個版型或多一個 phase 就編不過，而不是上線後某個畫面沒有高度。
const PANEL: { inline: Record<FrameView, PanelSpec>, popup: Record<FrameView, PopupPanelSpec> } = {
  inline: {
    'answering': { kind: 'capped', cap: { narrow: 530, wide: 528 } },
    'results': { kind: 'auto' },
    'thank-you': { kind: 'auto' },
  },
  popup: {
    'answering': { kind: 'by-hero', hero: { narrow: 692, wide: 796 }, plain: { narrow: 638, wide: 756 } },
    'results': { kind: 'fixed', height: { narrow: 730, wide: 756 } },
    'thank-you': { kind: 'fixed', height: { narrow: 730, wide: 756 } },
  },
}

// header 目標高只有 hero × 斷點兩維 —— answering 與 results/thank-you 的數字完全相同（同一份
// Figma 稿的 hero / compact 高）。CSS 以前把同一組值寫了四次，那是四個可以各自漂移的數字。
// 只給 popup（selector 都帶 :not(.frame--inline)）：這組值是 popup 那份 Figma 稿的 header 高，
// inline 的 header 沒有 hero 圖可撐，套上去就是在標題底下留一塊固定空白。以前 results /
// thank-you 少了那個排除，於是 inline 的 thank-you 卡片被 187 / 126 的
// header 佔掉一截：寬版位實測 header 盒子被釘在 187px，而它的自然高只有 113px，多出的 74px
// 全算進致謝插章上方，圖文因此在視覺上明顯偏下（留白上 139 / 下 62，量法見 HEADER_FIXED）。
// 哪些 phase 真的會拿到這組值，另見下面的 HEADER_FIXED。
// ⚠️ 這組值與 frame.ts 的 `.frame-header.has-hero { min-height }`（base 180 / container 300）
// 今天相同但**不是同一個約束**：那條是 hero 圖在所有版型（含收合卡）的保留高，這裡是面板
// header 的目標高。frame.test.ts 有一條測試釘住「今天相同」，只改一邊會有人喊。
// logo 載入失敗時 plain 就是這一組值（--mp-logo-* 歸零）；有 logo 時的 plain 高改由
// PLAIN_WITH_LOGO 與 plainHeaderValue() 算，hero 高與 logo 無關、不變。
const HEADER_HEIGHT: Record<'hero' | 'plain', ByBreakpoint> = {
  hero: { narrow: 180, wide: 300 },
  plain: { narrow: 126, wide: 187 },
}

// plain header（無封面或封面失敗）有 logo 時怎麼長高。logo 載入失敗時增量歸零、回到 HEADER_HEIGHT.plain。
//   narrow：logo 在 flow 內，header＝max(下限, plain＋logo 列)。下限 160 是 Figma 314:4171 的 min-h；
//           各 brand 實際高見 314:3092 160、810:31855 160.97、810:38902 182.63、810:45658 167.04、810:48347 180.61。
//   wide：logo 絕對定位、內容從 100 起排，與 brand 無關（Figma 810:26191 / 810:26479：255）。
//         frame.ts 桌機 padding-top 100 與這裡的增量（255 − 187 ＝ 100 − 32）由 frame.test.ts 釘住。
const PLAIN_WITH_LOGO: { narrow: { kind: 'row', floor: number }, wide: { kind: 'fixed', height: number } } = {
  narrow: { kind: 'row', floor: 160 },
  wide: { kind: 'fixed', height: 255 },
}

// popup 作答頁 plain 面板是否跟著 header 因 logo 長高：
//   narrow 跟：Figma 314:3091 等 frame＝Hero＋512（cars 810:31854 672.97、woman 810:48346 692.61）。
//   wide 不跟：Figma 314:4089 面板 756，Questionnaire 314:4092 由 569 縮為 501。
const PANEL_FOLLOWS_LOGO_HEADER: ByBreakpoint<boolean> = { narrow: true, wide: false }

// 這個 phase 的 header 要不要釘住上面那組固定高。
//
// thank-you 不釘。那組值是照「badge ＋ 最多兩行標題 ＋ 最多兩行說明」的保留高訂的，而致謝畫面
// 的內容只有插章加一行文案：盒子底部會空一塊，且那塊空白整個落在插章上方，於是整組圖文看起來
// 沒有置中——.thankyou 的 auto margin 其實一直是對稱的，偏掉的是它所在那個框的起點。寬版位實測
// header 盒子被釘在 187px，而它的自然高 inline 只有 113px、popup 165px。
//
// 留白的量法（下面各處的數字都用這一組，換基準就對不起來）：上＝致謝區塊上緣減 header 最後
// 一行文字下緣，下＝卡片下緣減致謝區塊下緣。910px 版位對照已發佈的 v1.0.0：inline 從 139 / 62
// 變成 98 / 103，modal 從 236.5 / 186.5 變成 219.5 / 203.5，sidebar 收合卡從 126.5 / 71.5 變成
// 104.5 / 93.5。殘餘差值是設計稿的內距 token（header 下內距、內容區上下內距、卡片邊框），
// .thankyou 在內容區內的 margin 上下相等。
//
// 有封面圖的致謝頁不受影響：hero 的保留高走 frame.ts 的 `.frame-header.has-hero { min-height }`，
// 不經過這裡。
//
// results 維持釘住——統計內容會把 body 填滿，header 底下那塊空白沒有對照物，不像致謝畫面那樣
// 讀得出來。它同時帶著上面那條 inline 排除，所以嚴格說是「popup 的結果頁維持釘住」；inline 的
// 結果頁今天渲染不到（survey-element 的三個 resultsFrameOpts 呼叫點不是覆寫成 thank-you 就是
// 傳 inline=false），那條排除因此是防未來的，不改變今天任何畫面。
const HEADER_FIXED: Record<FrameView, boolean> = {
  'answering': true,
  'results': true,
  'thank-you': false,
}

// 每個 phase 的垂直捲動擁有者。answering 由 body 捲（header 釘住不動、題目區自己捲）；
// results / thank-you 由整個 frame 捲（結果內容連 header 一起帶走）。「一個 phase 恰好一個
// 擁有者」是這個型別保證的，不是四條各自獨立的 CSS 宣告碰巧湊出來的。
const SCROLL_OWNER: Record<FrameView, ScrollOwner> = {
  'answering': 'body',
  'results': 'frame',
  'thank-you': 'frame',
}

export const GEOMETRY = { PANEL, HEADER_HEIGHT, HEADER_FIXED, SCROLL_OWNER, WIDE_PX, VIEWPORT_CAP_PCT, BODY_RESERVE_PX, LOGO, PLAIN_WITH_LOGO, PANEL_FOLLOWS_LOGO_HEADER }

// ---- CSS 生成 ----

// 「有 hero」在 DOM 上是 has-hero 且沒壞；「沒有 hero」是兩種情況的聯集，所以那一側一定是兩個
// selector 併寫。這是 selector 層的推導，不是查表時的推導 —— 資料表只認 hero / plain 兩格。
const HERO_SEL = '.has-hero:not([data-hero-broken])'
const PLAIN_SELS = [':not(.has-hero)', '.has-hero[data-hero-broken]']
// 面板高與 header 高都是 popup 那份 Figma 稿的數字，inline 一律排除（見 HEADER_HEIGHT 上方）。
const NOT_INLINE = ':not(.frame--inline)'

const VIEWS: FrameView[] = ['answering', 'results', 'thank-you']
type Breakpoint = keyof ByBreakpoint

// 規格相同的 phase 併成同一條規則。分組而不是寫死「results 與 thank-you 一組」——那樣的話
// 有人把 thank-you 的高度改掉，生成器會安靜地沿用 results 的值。
interface ViewGroup { head: FrameView, views: FrameView[] }

function groupViews(sameSpec: (a: FrameView, b: FrameView) => boolean): ViewGroup[] {
  const groups: ViewGroup[] = []
  for (const v of VIEWS) {
    const hit = groups.find(g => sameSpec(g.head, v))
    if (hit)
      hit.views.push(v)
    else groups.push({ head: v, views: [v] })
  }
  return groups
}

// plain header 有 logo 時的目標高：narrow 是「下限與 plain＋logo 列取大」，wide 是「plain 固定高
// 改成 logo 版固定高」的位移。logo 失敗時 --mp-logo-* 全歸零，算出來的值等於 HEADER_HEIGHT.plain。
function plainHeaderValue(bp: Breakpoint): string {
  const spec = PLAIN_WITH_LOGO[bp]
  return spec.kind === 'row'
    ? `max(var(--mp-logo-floor), calc(${HEADER_HEIGHT.plain[bp]}px + var(--mp-logo-row)))`
    : `calc(${HEADER_HEIGHT.plain[bp]}px + var(--mp-logo-top-shift))`
}

function headerRules(views: FrameView[], bp: Breakpoint): string {
  const fixed = views.filter(v => HEADER_FIXED[v])
  if (fixed.length === 0)
    return ''
  const sel = (hero: boolean): string =>
    fixed.flatMap(v => hero ? [`.frame--${v}${HERO_SEL}${NOT_INLINE}`] : PLAIN_SELS.map(x => `.frame--${v}${x}${NOT_INLINE}`)).join(',\n')
  return [
    `${sel(true)} { --target-header-height: ${HEADER_HEIGHT.hero[bp]}px; }`,
    `${sel(false)} { --target-header-height: ${plainHeaderValue(bp)}; }`,
  ].join('\n')
}

// popup（:not(.frame--inline)）的面板與 header 高。answering 走 by-hero，兩個高度同一條規則
// 出；results / thank-you 是 fixed，面板高一條、header 高另外兩條。三個 phase 的 header 高都
// 帶 inline 排除，inline 的 header 因此跟著內容長。
function popupRules(bp: Breakpoint): string {
  const out: string[] = []
  for (const { head, views: group } of groupViews((a, b) => JSON.stringify(PANEL.popup[a]) === JSON.stringify(PANEL.popup[b]))) {
    const spec = PANEL.popup[head]
    if (spec.kind === 'auto')
      continue
    if (spec.kind === 'by-hero') {
      out.push(
        `${group.map(v => `.frame--${v}${HERO_SEL}${NOT_INLINE}`).join(',\n')} {`,
        `  --target-panel-height: ${spec.hero[bp]}px;`,
        `  --target-header-height: ${HEADER_HEIGHT.hero[bp]}px;`,
        `}`,
        `${group.flatMap(v => PLAIN_SELS.map(x => `.frame--${v}${x}${NOT_INLINE}`)).join(',\n')} {`,
        `  --target-header-height: ${plainHeaderValue(bp)};`,
        PANEL_FOLLOWS_LOGO_HEADER[bp]
          ? `  --target-panel-height: calc(${spec.plain[bp] - HEADER_HEIGHT.plain[bp]}px + var(--target-header-height));`
          : `  --target-panel-height: ${spec.plain[bp]}px;`,
        `}`,
      )
      continue
    }
    out.push(
      `${group.map(v => `.frame--${v}${NOT_INLINE}`).join(',\n')} {`,
      `  --target-panel-height: ${spec.height[bp]}px;`,
      `}`,
    )
    // 這一組裡沒有任何釘固定高的 phase 時 headerRules 回空字串，不要把空行推進輸出。
    const header = headerRules(group, bp)
    if (header)
      out.push(header)
  }
  return out.join('\n')
}

// inline 的面板高。fixed 輸出固定高；capped 只輸出上限變數（刻意不輸出 --target-panel-height，
// 見 PanelSpec）；auto 什麼都不輸出。
function inlineRules(bp: Breakpoint): string {
  return VIEWS
    .map((v) => {
      const spec = PANEL.inline[v]
      switch (spec.kind) {
        case 'fixed': return `.frame--inline.frame--${v} { --target-panel-height: calc(${spec.height[bp]}px + var(--mp-logo-row)); }`
        case 'capped': return `.frame--inline.frame--${v} { --target-panel-cap: calc(${spec.cap[bp]}px + var(--mp-logo-row)); }`
        default: return ''
      }
    })
    .filter(Boolean)
    .join('\n')
}

// capped 的 phase 怎麼用上限變數（讀變數，不分斷點，所以只輸出一次）：
//   max-height 把面板夾在「上限」與「viewport 比例」的較小者（vh 先於 dvh，理由同 VIEWPORT_CAP_PCT），
//   再扣掉 peek.ts 在當前題放不下時寫在 .frame 上的 --peek-inset：放不下的題面板一定頂在上限，扣掉
//   之後最後一列可見選項的文字剛好落在淡出帶裡，作答區與按鈕列之間不留白。
//   header 的 320px 防線：面板高是 auto，scrollRules 裡那條 `100% - 320px` 的百分比算不出來
//   （等於沒設），所以 inline 另以上限為基準重算同一個數字。selector 多帶一個 .frame--inline，
//   特異度必然壓過 scrollRules 的那條，不靠宣告順序；popup 的那條不受影響。
function inlineCapRules(): string {
  const out: string[] = []
  for (const v of VIEWS) {
    if (PANEL.inline[v].kind !== 'capped')
      continue
    const cap = (unit: 'vh' | 'dvh'): string => `min(var(--target-panel-cap), ${VIEWPORT_CAP_PCT}${unit})`
    out.push(
      `.frame--inline.frame--${v} {`,
      `  max-height: calc(${cap('vh')} - var(--peek-inset, 0px));`,
      `  max-height: calc(${cap('dvh')} - var(--peek-inset, 0px));`,
      `}`,
      `.frame.frame--inline.frame--${v} .frame-header {`,
      `  max-height: max(0px, calc(${cap('vh')} - ${BODY_RESERVE_PX}px));`,
      `  max-height: max(0px, calc(${cap('dvh')} - ${BODY_RESERVE_PX}px));`,
      `}`,
    )
  }
  return out.join('\n')
}

// 捲動規則依擁有者分組產出。body 擁有者：外層不捲、header 釘住、body 自己捲（所以那組還帶著
// 「flex column ＋ header 定高」的結構宣告）。frame 擁有者：整個面板捲，body 不另外捲。
// 分組而不是寫死哪些 phase 屬於哪一組 —— 寫死的話改了 SCROLL_OWNER 的某一格，CSS 不會跟著動，
// 而 jsdom 不排版，沒有任何閘門會發現資料表與實際樣式各走各的。
// 消費 --target-header-height 的那一側，selector 必須與供應側（headerRules）用同一組條件：
// 只給 popup、且只給釘固定高的 phase。兩側各排一次是刻意的重複——只排供應側的話，未被排除的
// 那些仍會命中 `height: var(--target-header-height)`，全憑「變數未定義 → 宣告 invalid → 退回
// auto」這條間接路徑生效；哪天有人在別處定義了那個變數（custom property 還會從祖先繼承），
// 版面偏移就會安靜地回來，而 jsdom 不排版、snapshot 只比字串，沒有閘門攔得住。
function headerSel(group: FrameView[]): string {
  return group
    .filter(v => HEADER_FIXED[v])
    .map(v => `.frame.frame--${v}${NOT_INLINE} .frame-header`)
    .join(',\n')
}

function scrollRules(): string {
  const reserve = `max(0px, calc(100% - ${BODY_RESERVE_PX}px))`
  const out: string[] = []
  for (const { head, views: group } of groupViews((a, b) => SCROLL_OWNER[a] === SCROLL_OWNER[b])) {
    const sel = group.map(v => `.frame--${v}`).join(',\n')
    const scoped = (suffix: string): string => group.map(v => `.frame.frame--${v} ${suffix}`).join(',\n')
    if (SCROLL_OWNER[head] === 'body') {
      out.push(
        `${sel} {`,
        `  display: flex;`,
        `  flex-direction: column;`,
        `  overflow: hidden;`,
        `}`,
        `${scoped('.frame-header')} {`,
        `  box-sizing: border-box;`,
        `  flex: none;`,
        `  min-height: 0;`,
        `  max-height: ${reserve};`,
        `  overflow: hidden;`,
        `}`,
        `${headerSel(group)} {`,
        `  height: min(var(--target-header-height), ${reserve});`,
        `}`,
        `${scoped('.frame-body')} {`,
        `  flex: 1 1 auto;`,
        `  min-height: 0;`,
        `  display: flex;`,
        `  flex-direction: column;`,
        `  overflow-y: auto;`,
        `}`,
      )
      continue
    }
    out.push(
      `${sel} {`,
      `  overflow-y: auto;`,
      `}`,
      `${scoped('.frame-header')} {`,
      `  box-sizing: border-box;`,
      `}`,
      `${scoped('.frame-body')} {`,
      `  overflow: visible;`,
      `}`,
    )
    const fixedSel = headerSel(group)
    if (fixedSel) {
      out.push(
        `${fixedSel} {`,
        `  height: var(--target-header-height);`,
        `  min-height: var(--target-header-height);`,
        `}`,
      )
    }
  }
  return out.join('\n')
}

// --mp-logo-* 三個變數：popup／收合卡 row gap 用 popup 值，inline 覆寫成自己的 row gap；
// logo 載入失敗時（.frame[data-logo-broken]，specificity 較高、後到）全部歸零，plainHeaderValue()
// 與 inlineRules() 算出來的值因此退回 HEADER_HEIGHT.plain／PANEL.inline 的原始面板高。
function logoVarsCss(): string {
  return [
    `.frame {`,
    `  --mp-logo-row: calc(${LOGO.rowGap.popup}px + ${LOGO.inFlowWidth}px * var(--mp-logo-aspect));`,
    `  --mp-logo-floor: ${PLAIN_WITH_LOGO.narrow.floor}px;`,
    `  --mp-logo-top-shift: ${PLAIN_WITH_LOGO.wide.height - HEADER_HEIGHT.plain.wide}px;`,
    `}`,
    `.frame--inline { --mp-logo-row: calc(${LOGO.rowGap.inline}px + ${LOGO.inFlowWidth}px * var(--mp-logo-aspect)); }`,
    `.frame[data-logo-broken] {`,
    `  --mp-logo-row: 0px;`,
    `  --mp-logo-floor: 0px;`,
    `  --mp-logo-top-shift: 0px;`,
    `}`,
  ].join('\n')
}

export function geometryCss(): string {
  const phaseSel = VIEWS.map(v => `.frame--${v}`).join(',\n')
  return `
${phaseSel} {
  width: 100%;
  height: min(var(--target-panel-height), ${VIEWPORT_CAP_PCT}vh);
  height: min(var(--target-panel-height), ${VIEWPORT_CAP_PCT}dvh);
}
${logoVarsCss()}
${inlineRules('narrow')}
${popupRules('narrow')}
${scrollRules()}
${inlineCapRules()}
@media (min-width: ${WIDE_PX}px) {
${inlineRules('wide')}
${popupRules('wide')}
}
`.trim()
}
